import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import {
    CheckboxChecked,
    CheckboxUnchecked,
} from '@resurs-internal/mui/components/SelectCheckboxField/SelectCheckboxField';
import { ChangeEvent } from 'react';

import { Debt } from '../../api/getDebts';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { getAccountTypeTranslationKey } from '../../helpers/getAccountTypeTranslationKey';
import useTranslation from '../../hooks/useTranslation';

type Props = {
    debts: Debt[];
    handleChange: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
    selectedConsolidations: Debt[];
};

const ConsolidationCheckboxes = ({ debts, handleChange, selectedConsolidations }: Props) => {
    const translate = useTranslation();

    const getTitle = (debt: Debt) => {
        let result = translate(getAccountTypeTranslationKey(debt.accountType));

        if (debt.creditorName) {
            result += ', ' + debt.creditorName;
        }

        if (debt.currentBalance) {
            result +=
                ', ' + formatNumberByThousands(debt.currentBalance.amount) + ' ' + translate('field.currency.label');
        }
        return result;
    };

    const getText = (debt: Debt) => {
        let result = '';

        if (debt.nominalInterestRate) {
            result = `${debt.nominalInterestRate}% ${translate('field.interest.label')}`;
        }

        if (debt.tenor) {
            if (result) {
                result += ', ';
            }

            result += `${debt.tenor} ${translate('field.months.label')}`;
        }

        return result;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
            {debts?.map((debt) => (
                <FormControlLabel
                    key={debt.internalId}
                    label={
                        <>
                            <Typography>{getTitle(debt)}</Typography>
                            <Typography variant="caption">{getText(debt)}</Typography>
                        </>
                    }
                    control={
                        <Checkbox
                            name="consolidateOtherLoans"
                            onChange={(event) => handleChange(event, debt.internalId)}
                            value={debt.internalId}
                            checked={
                                !!selectedConsolidations.find(
                                    (consolidation) => debt.internalId === consolidation.internalId,
                                ) || debt.nonSelectable
                            }
                            icon={<CheckboxUnchecked />}
                            checkedIcon={<CheckboxChecked />}
                            disabled={debt.nonSelectable}
                        />
                    }
                />
            ))}
        </Box>
    );
};

export default ConsolidationCheckboxes;
